import { WidgetLoadError, WidgetLoadFailureReason, type WidgetLoadDiagnostics } from "../types";

type IframeLoadMonitorConfig = {
    iframe: HTMLIFrameElement;
    expectedOrigin: string;
    onError: (error: WidgetLoadError) => void;
};

export type IframeLoadMonitor = {
    destroy: () => void;
    diagnose: () => WidgetLoadError | null;
};

const formatMessage = (
    reason: WidgetLoadFailureReason,
    expectedOrigin: string,
    diagnostics: WidgetLoadDiagnostics,
): string => {
    switch (reason) {
        case WidgetLoadFailureReason.CSP_BLOCKED:
            return `Widget load failed: Content Security Policy blocked the widget URL. Add '${expectedOrigin}' to your frame-src or child-src CSP directive. Blocked URI: ${diagnostics.cspBlockedURI}`;
        case WidgetLoadFailureReason.NETWORK_FAILURE:
            return `Widget load failed: the request to '${expectedOrigin}' was made but did not complete. This may indicate DNS resolution failure, server unavailability, or a network connectivity issue.`;
        case WidgetLoadFailureReason.IFRAME_REMOVED:
            return "Widget load failed: iframe was removed from the DOM. This commonly happens with SPA routers, HMR, or cleanup code that removes the iframe before the widget finishes loading.";
        case WidgetLoadFailureReason.RESOURCE_BLOCKED:
            return `Widget load failed: the iframe request was blocked before reaching the network. This is commonly caused by ad blockers, privacy extensions, or corporate proxy/firewall rules that block requests to '${expectedOrigin}'.`;
        case WidgetLoadFailureReason.UNKNOWN:
            return `Widget load failed: unable to determine the specific reason. Diagnostics: onload=${diagnostics.onloadFired}, inDOM=${diagnostics.iframeInDOM}, resourceEntry=${diagnostics.resourceTimingEntryExists}, csp=${diagnostics.cspViolationDetected}`;
    }
};

export const monitorIframeLoad = (config: IframeLoadMonitorConfig): IframeLoadMonitor => {
    const { iframe, expectedOrigin, onError } = config;

    let destroyed = false;
    let errorReported = false;
    let onloadFired = false;
    let cspViolationDetected = false;
    let cspBlockedURI: string | null = null;
    const startTime = Date.now();

    // --- Internal helpers ---

    const collectDiagnostics = (): WidgetLoadDiagnostics => {
        const iframeInDOM = document.contains(iframe);
        let resourceTimingEntryExists = false;
        try {
            const urlBase = iframe.src.split("?")[0] ?? "";
            if (urlBase && typeof performance !== "undefined" && typeof performance.getEntriesByType === "function") {
                const entries = performance.getEntriesByType("resource");
                resourceTimingEntryExists = entries.some((e) => e.name.startsWith(urlBase));
            }
        } catch {
            /* Resource Timing not available */
        }
        return {
            onloadFired,
            cspViolationDetected,
            cspBlockedURI,
            iframeInDOM,
            resourceTimingEntryExists,
            elapsedMs: Date.now() - startTime,
        };
    };

    const buildError = (reason: WidgetLoadFailureReason): WidgetLoadError => {
        const diagnostics = collectDiagnostics();
        const message = formatMessage(reason, expectedOrigin, diagnostics);
        return new WidgetLoadError(message, reason, diagnostics);
    };

    const reportError = (reason: WidgetLoadFailureReason) => {
        if (errorReported || destroyed) return;
        errorReported = true;
        const error = buildError(reason);
        destroy();
        onError(error);
    };

    // --- Listeners ---

    // 1. CSP violation
    const handleCSPViolation = (event: SecurityPolicyViolationEvent) => {
        if (event.blockedURI && event.blockedURI.startsWith(expectedOrigin)) {
            cspViolationDetected = true;
            cspBlockedURI = event.blockedURI;
            reportError(WidgetLoadFailureReason.CSP_BLOCKED);
        }
    };
    document.addEventListener("securitypolicyviolation", handleCSPViolation);

    // 2. Iframe removal (MutationObserver on parent)
    let observer: MutationObserver | null = null;
    try {
        if (typeof MutationObserver !== "undefined" && iframe.parentElement) {
            observer = new MutationObserver((mutations) => {
                try {
                    for (const mutation of mutations) {
                        for (let i = 0; i < mutation.removedNodes.length; i++) {
                            if (mutation.removedNodes[i] === iframe) {
                                reportError(WidgetLoadFailureReason.IFRAME_REMOVED);
                                return;
                            }
                        }
                    }
                } catch {
                    // Ignore errors in test environments (jsdom)
                }
            });
            observer.observe(iframe.parentElement, { childList: true });
        }
    } catch {
        // MutationObserver not available or parent not observable
    }

    // 3. Onload tracking
    const handleLoad = () => {
        onloadFired = true;
    };
    iframe.addEventListener("load", handleLoad);

    // --- Public methods ---

    const diagnose = (): WidgetLoadError | null => {
        if (errorReported) return null;
        const diagnostics = collectDiagnostics();
        if (!diagnostics.iframeInDOM) return buildError(WidgetLoadFailureReason.IFRAME_REMOVED);
        if (diagnostics.cspViolationDetected) return buildError(WidgetLoadFailureReason.CSP_BLOCKED);
        if (!diagnostics.onloadFired && !diagnostics.resourceTimingEntryExists)
            return buildError(WidgetLoadFailureReason.RESOURCE_BLOCKED);
        if (!diagnostics.onloadFired && diagnostics.resourceTimingEntryExists)
            return buildError(WidgetLoadFailureReason.NETWORK_FAILURE);
        return null;
    };

    const destroy = () => {
        if (destroyed) return;
        destroyed = true;
        document.removeEventListener("securitypolicyviolation", handleCSPViolation);
        iframe.removeEventListener("load", handleLoad);
        observer?.disconnect();
    };

    return { destroy, diagnose };
};
